import './index.css'

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure
} from '@chakra-ui/react'
import { createPopup } from '@picmo/popup-picker'
import { Editor } from '@tiptap/react'
import ptEmojiData from 'emojibase-data/pt/data.json'
import { Fragment, RefObject, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiParagraph } from 'react-icons/bi'
import {
  MdFormatItalic,
  MdFormatStrikethrough,
  MdFormatUnderlined,
  MdLink
} from 'react-icons/md'
import {
  RiH1,
  RiH2,
  RiH3,
  RiListOrdered,
  RiListUnordered
} from 'react-icons/ri'

type MenuBarItem = {
  icon: JSX.Element
  title: string
  action: () => void
  isActive: () => boolean
  isDisabled?: () => boolean
}

export default function Menubar({
  editor,
  markdownEditorRef
}: {
  editor: Editor
  markdownEditorRef?: RefObject<HTMLDivElement>
}) {
  const {
    isOpen: isLinkOverlayOpen,
    onClose: onLinkOverlayClose,
    onOpen: onLinkOverlayOpen
  } = useDisclosure()
  const { t } = useTranslation()

  const [linkText, setLinkText] = useState('')
  const [hasExistentText, setHasExistentText] = useState(false)
  const [linkHref, setLinkHref] = useState('')

  const items = [
    {
      icon: (
        <span
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontWeight: 'bold'
          }}
        >
          N
        </span>
      ),
      title: t('richtext.settings.header.bold'),
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
      isDisabled: () => editor.isActive('link')
    },
    {
      icon: <MdFormatItalic />,
      title: t('richtext.settings.header.italic'),
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive('italic'),
      isDisabled: () => editor.isActive('link')
    },
    {
      icon: <MdFormatStrikethrough />,
      title: t('richtext.settings.header.strike'),
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive('strike'),
      isDisabled: () => editor.isActive('link')
    },
    {
      icon: <MdFormatUnderlined />,
      title: t('richtext.settings.header.underline'),
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: () => editor.isActive('underline'),
      isDisabled: () => editor.isActive('link')
    },
    {
      icon: <MdLink />,
      title: t('richtext.settings.header.link'),
      action: () => {
        const textContent =
          editor.getAttributes('link')['data-text-content'] ?? ''
        const previousHref = editor.getAttributes('link').href ?? ''

        if (previousHref)
          return editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .unsetLink()
            .run()

        const previousText =
          textContent ||
          editor.view.state.doc.textBetween(
            editor.view.state.selection.from,
            editor.view.state.selection.to,
            ''
          )

        setHasExistentText(!!previousText)
        setLinkText(previousText)
        onLinkOverlayOpen()
      },
      isActive: () => editor.isActive('link')
    },
    {
      type: 'divider'
    },
    {
      icon: <RiH1 />,
      title: t('richtext.settings.header.heading_1'),
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive('heading', { level: 1 })
    },
    {
      icon: <RiH2 />,
      title: t('richtext.settings.header.heading_2'),
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive('heading', { level: 2 })
    },
    {
      icon: <RiH3 />,
      title: t('richtext.settings.header.heading_3'),
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive('heading', { level: 3 })
    },
    {
      icon: <BiParagraph />,
      title: t('richtext.settings.header.paragraph'),
      action: () => editor.chain().focus().setParagraph().run(),
      isActive: () => editor.isActive('paragraph')
    },
    {
      type: 'divider'
    },
    {
      icon: <RiListUnordered />,
      title: t('richtext.settings.header.bullet_list'),
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList')
    },
    {
      icon: <RiListOrdered />,
      title: t('richtext.settings.header.ordered_list'),
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive('orderedList')
    }
  ] satisfies ({ type: string } | MenuBarItem)[]

  const [emojiCharacter, setEmojiCharacter] = useState('😀')
  const emojiButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!emojiButtonRef.current || !markdownEditorRef?.current) return

    const popup = createPopup(
      {
        emojiData: ptEmojiData,
        i18n: {
          'categories.activities': t('emoji.categories.activities'),
          'categories.animals-nature': t('emoji.categories.animals-nature'),
          'categories.custom': t('emoji.categories.custom'),
          'categories.flags': t('emoji.categories.flags'),
          'categories.food-drink': t('emoji.categories.food-drink'),
          'categories.objects': t('emoji.categories.objects'),
          'categories.people-body': t('emoji.categories.people-body'),
          'categories.recents': t('emoji.categories.recents'),
          'categories.smileys-emotion': t('emoji.categories.smileys-emotion'),
          'categories.symbols': t('emoji.categories.symbols'),
          'categories.travel-places': t('emoji.categories.travel-places'),
          'error.load': t('emoji.error.load'),
          'recents.clear': t('emoji.recents.clear'),
          'recents.none': t('emoji.recents.none'),
          retry: t('emoji.retry'),
          'search.clear': t('emoji.search.clear'),
          'search.error': t('emoji.search.error'),
          'search.notFound': t('emoji.search.notFound'),
          search: t('emoji.search.label')
        },
        locale: 'pt',
        showPreview: false,
        className: 'markdown-emoji'
      },
      {
        triggerElement: emojiButtonRef.current,
        referenceElement: markdownEditorRef.current,
        hideOnEscape: true,
        hideOnClickOutside: true,
        showCloseButton: false,
        onPositionLost: 'destroy',
        position: 'right-start'
      }
    )

    function toggleTrigger() {
      popup.toggle()
    }

    emojiButtonRef.current.addEventListener('click', toggleTrigger)

    function addEmoji(selection: { emoji: string }) {
      setEmojiCharacter(selection.emoji)
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.insertText(selection.emoji)
          return true
        })
        .run()
    }

    popup.addEventListener('emoji:select', addEmoji)

    return () => {
      emojiButtonRef.current?.removeEventListener('click', toggleTrigger)
      popup.removeEventListener('emoji:select', addEmoji)
    }
  }, [emojiButtonRef, markdownEditorRef])

  return (
    <Box className="editor__header">
      {items.map(
        ({ action, icon, isActive, title, type, isDisabled }, index) => {
          return (
            <Fragment key={index}>
              {type === 'divider' ? (
                <Box
                  bgColor={'rgba(#fff, 0.25)'}
                  h={'1.25rem'}
                  ml={'0.5rem'}
                  mr={'0.75rem'}
                  w={'1px'}
                />
              ) : title === 'Link' ? (
                <Popover
                  isOpen={isLinkOverlayOpen}
                  onClose={() => {
                    onLinkOverlayClose()
                    setHasExistentText(false)
                    setLinkText('')
                    setLinkHref('')
                  }}
                  matchWidth
                  placement="bottom"
                >
                  <PopoverTrigger>
                    <button
                      type="button"
                      className={`menu-item${
                        isActive && isActive() ? ' is-active' : ''
                      }`}
                      onClick={action}
                      title={title}
                    >
                      {icon}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent w="fit-content">
                    <PopoverBody
                      display={'flex'}
                      flexDir={'column'}
                      p={'2'}
                      borderRadius={'md'}
                      gap={'2'}
                      w={'60'}
                    >
                      <FormControl>
                        <FormLabel>
                          {t('richtext.settings.link.text')}
                        </FormLabel>
                        <Input
                          value={linkText}
                          isDisabled={hasExistentText}
                          onChange={(e) => setLinkText(e.currentTarget.value)}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>
                          {t('richtext.settings.link.link')}
                        </FormLabel>
                        <Input
                          value={linkHref}
                          onChange={(e) => setLinkHref(e.currentTarget.value)}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        colorScheme="blue"
                        onClick={() => {
                          editor.commands.unsetBold()
                          editor.commands.unsetItalic()
                          editor.commands.unsetStrike()
                          editor.commands.unsetUnderline()
                          if (!linkHref) {
                            editor
                              .chain()
                              .focus()
                              .extendMarkRange('link')
                              .unsetLink()
                              .run()
                          } else {
                            if (hasExistentText) {
                              editor
                                .chain()
                                .focus()
                                .extendMarkRange('link')
                                .unsetAllMarks()
                                .setLink({
                                  rel: 'noopener noreferrer',
                                  target: '__blank',
                                  href: linkHref
                                })
                                .run()
                            } else {
                              editor
                                .chain()
                                .focus()
                                .extendMarkRange('link')
                                .unsetAllMarks()
                                .setLink({
                                  rel: 'noopener noreferrer',
                                  target: '__blank',
                                  href: linkHref
                                })
                                .command(({ tr }) => {
                                  tr.insertText(linkText)
                                  return true
                                })
                                .run()
                            }
                          }
                          onLinkOverlayClose()
                          setHasExistentText(false)
                          setLinkText('')
                          setLinkHref('')
                        }}
                      >
                        {t('richtext.settings.link.create')}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          editor
                            .chain()
                            .focus()
                            .extendMarkRange('link')
                            .unsetLink()
                            .run()

                          onLinkOverlayClose()
                          setHasExistentText(false)
                          setLinkText('')
                          setLinkHref('')
                        }}
                      >
                        {t('richtext.settings.link.remove')}
                      </Button>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : (
                <button
                  type="button"
                  className={`menu-item${
                    isDisabled && isDisabled()
                      ? ' is-disabled'
                      : isActive && isActive()
                        ? ' is-active'
                        : ''
                  }`}
                  onClick={action}
                  title={title}
                  disabled={isDisabled && isDisabled()}
                >
                  {icon}
                </button>
              )}
            </Fragment>
          )
        }
      )}
      <button
        type="button"
        id={'emoji-button'}
        className={'menu-item'}
        title={'emoji'}
        ref={emojiButtonRef}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {emojiCharacter}
      </button>
    </Box>
  )
}
